import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  BusinessUnitReportFilters,
  CompanyReportFilters,
  MerchantReportFilters,
  UserReportFilters,
  OperatorReportFilters,
  DispersionReportFilters,
  DispersionReportVariant,
  DispersionVariantReportFilters,
  SettlementReportFilters,
  TerminalReportFilters,
  TpvReportRepository,
} from '@app/domain/ports/tpv/tpv-report.repository';
import { TransactionListQuery } from '@app/domain/ports/tpv/transaction.repository';
import { TpvReportFile, TpvReportFormat } from '@app/domain/models/tpv/tpv-report.model';

// Base64 de un texto corto ("mock report") — suficiente para validar el flujo de
// decodificación/descarga sin backend. NO es un xlsx/pdf real.
const MOCK_BASE64 = 'bW9jayByZXBvcnQ=';

/** Data source mock de reportes TPV (dev/testing): simula latencia y archivo en Base64. */
@Injectable()
export class TpvReportMockDataSource extends TpvReportRepository {
  override getTransactionsReport(
    format: TpvReportFormat,
    _externalIdMerchant: string | undefined,
    _query: TransactionListQuery,
    breakdown?: boolean,
  ): Observable<TpvReportFile> {
    return this.mock(breakdown ? 'transacciones_desglose' : 'transacciones', format);
  }

  override getMerchantsReport(format: TpvReportFormat, _filters: MerchantReportFilters): Observable<TpvReportFile> {
    return this.mock('comercios', format);
  }

  override getCompaniesReport(format: TpvReportFormat, _filters: CompanyReportFilters): Observable<TpvReportFile> {
    return this.mock('empresas', format);
  }

  override getBusinessUnitsReport(format: TpvReportFormat, _filters: BusinessUnitReportFilters): Observable<TpvReportFile> {
    return this.mock('unidades', format);
  }

  override getUsersReport(format: TpvReportFormat, _filters: UserReportFilters): Observable<TpvReportFile> {
    return this.mock('usuarios', format);
  }

  override getOperatorsReport(
    format: TpvReportFormat,
    _idCompany: number | string,
    _filters: OperatorReportFilters,
  ): Observable<TpvReportFile> {
    return this.mock('operadores', format);
  }

  override getDispersionsReport(format: TpvReportFormat, _filters: DispersionReportFilters): Observable<TpvReportFile> {
    return this.mock('dispersiones', format);
  }

  override getDispersionsVariantReport(
    variant: DispersionReportVariant,
    format: TpvReportFormat,
    _filters: DispersionVariantReportFilters,
  ): Observable<TpvReportFile> {
    return this.mock(`dispersiones_${variant}`, format);
  }

  override getSettlementsReport(format: TpvReportFormat, _filters: SettlementReportFilters): Observable<TpvReportFile> {
    return this.mock('liquidaciones', format);
  }

  override getTerminalsReport(format: TpvReportFormat, _filters: TerminalReportFilters): Observable<TpvReportFile> {
    return this.mock('terminales', format);
  }

  private mock(slug: string, format: TpvReportFormat): Observable<TpvReportFile> {
    const ext = format === 'excel' ? 'xlsx' : format;
    return of<TpvReportFile>({
      fileName: `mock_${slug}.${ext}`,
      fileBase64: MOCK_BASE64,
      fileSize: 11,
    }).pipe(delay(300));
  }
}
